// What fills a widget: for each kind of number or list a starter widget asks
// for, the services whose ready-made read fills it, in the order they are
// offered. Connect on such a widget leads with them, and connecting one wires
// the widget at once: the read is made into the widget's own stream.

import { SPEC } from "./catalog";
import { RECIPE, type RecipeSpec } from "./presets";
import { KEY_SERVICES, recipesOf } from "./keyServices";

/** Each saved-key service's word for what it is read with: a key, or a token. */
const KEY_NOUN: Record<string, string> = Object.fromEntries(KEY_SERVICES.map((k) => [k.id, k.noun]));
import type { WidgetType } from "./types";

const FITS: Record<string, string[]> = {
  mrr: ["lemonsqueezy-mrr", "stripe-mrr"],
  orders: ["lemonsqueezy-order-count", "lemonsqueezy-orders"],
  revenue: ["lemonsqueezy-revenue", "stripe-revenue"],
  payments: ["stripe-payment-count", "lemonsqueezy-payments"],
  visitors: ["plausible-visitors"],
  "visitors-today": ["plausible-today"],
  "top-pages": ["plausible-pages"],
  referrers: ["plausible-sources"],
  subscribers: ["beehiiv-subs", "buttondown-subs"],
  "youtube-subs": ["youtube-subs"],
  balance: ["stripe-balance"],
};

/** A widget whose stream is none of those, by its name: the first that matches. */
const BY_WORDS: [RegExp, string][] = [
  [/\bmrr\b|\barr\b|recurring/i, "mrr"],
  [/\borders?\b|\bsales\b/i, "orders"],
  [/revenue|income|earnings|takings/i, "revenue"],
  [/payments?\b/i, "payments"],
  [/youtube/i, "youtube-subs"],
  [/visitors? today/i, "visitors-today"],
  [/top pages|popular pages/i, "top-pages"],
  [/referr|came from|traffic sources/i, "referrers"],
  [/visitor|traffic/i, "visitors"],
  [/subscriber|newsletter/i, "subscribers"],
  [/stripe balance|\bbalance\b/i, "balance"],
];

/** What a ready-made read gives: one number, a list or a table. */
const shapeOf = (r: RecipeSpec) => (r.extract.mode === "value" ? "number" : r.extract.mode === "list" ? "list" : "table");

/** The ready-made reads that fill a widget, best first: none for a widget that keeps its own content, or that no read fits. */
export function fitsFor(w: { type: WidgetType; title?: string | null; stream?: string | null }): RecipeSpec[] {
  const reads = SPEC[w.type]?.reads;
  if (!reads) return [];
  const key = w.stream && Object.hasOwn(FITS, w.stream) ? w.stream : BY_WORDS.find(([re]) => re.test(`${w.title ?? ""} ${w.stream ?? ""}`))?.[1];
  if (!key) return [];
  return FITS[key].map((id) => RECIPE[id]).filter((r): r is RecipeSpec => !!r && !r.quiet && reads.includes(shapeOf(r)));
}

/**
 * A service's reads Connect offers a widget, the ones that fill it first: a
 * goal, a Number or a Chart is offered only reads that give one number, and a
 * table only tables and lists, so Connect never wires a widget to data it
 * cannot draw. With no widget, all it offers.
 */
export function readsFor(w: { type: WidgetType; title?: string | null; stream?: string | null } | null, service: string): RecipeSpec[] {
  const all = recipesOf(service).filter((r) => !r.quiet);
  if (!w) return all;
  const reads = SPEC[w.type]?.reads;
  const fit = new Set(fitsFor(w).map((r) => r.id));
  const shown = reads ? all.filter((r) => reads.includes(shapeOf(r))) : all;
  return [...shown.filter((r) => fit.has(r.id)), ...shown.filter((r) => !fit.has(r.id))];
}

/** The one of a service's reads that fills a widget, if one does. */
export function fitFrom(w: { type: WidgetType; title?: string | null; stream?: string | null } | null, service: string): RecipeSpec | null {
  return w ? (fitsFor(w).find((r) => r.service === service) ?? null) : null;
}

/** "Lemon Squeezy or Stripe": the services that fill a widget, in words. */
export function fitNames(fits: RecipeSpec[]): string {
  const names = [...new Set(fits.map((r) => r.provider))];
  return names.length < 2 ? (names[0] ?? "") : `${names.slice(0, -1).join(", ")} or ${names[names.length - 1]}`;
}

/** "your MRR", "your orders": what a read shows, as a widget waiting for it says it. */
function yours(r: RecipeSpec): string {
  const words = r.label.replace(/,.*$/, "").replace(/\s*\(.*\)$/, "");
  return `your ${words.split(" ").map((w) => (w === w.toUpperCase() ? w : w.toLowerCase())).join(" ")}`;
}

/**
 * A data widget's first step (src/widgets/setup.tsx), in words: what it
 * shows once connected, which service fills it and how, and its one button.
 * "Your MRR" / "It stays current by itself. Needs an API key from one of
 * them, kept on the board and never shown." / "Connect Lemon Squeezy or
 * Stripe". service: the one service that fills it, whose own form
 * the button opens. A goal, a Number or a Chart that nothing fills by its name
 * says what it can show; any other widget has no first step of its own here.
 */
export function firstStep(w: { type: WidgetType; title?: string | null; stream?: string | null }): { title: string; sub: string; button: string; service: string | null } | null {
  const fits = fitsFor(w);
  if (fits.length) {
    const first = fits[0];
    const counts = w.type === "goal" && !!first.compose && /-(order-count|payment-count|revenue)$/.test(first.compose);
    const what = `${yours(first)}${counts ? " this month" : ""}`;
    const token = fits.every((r) => KEY_NOUN[r.service] === "token");
    const services = [...new Set(fits.map((r) => r.service))];
    const names = fitNames(fits);
    return {
      title: w.type === "goal" ? `Track ${what}` : what.charAt(0).toUpperCase() + what.slice(1),
      // the title says what it shows and the button which service: the words say how it connects, short enough for a small tile
      sub: `It stays current by itself. Needs ${services.length > 1 ? "an API key from one of them" : `your ${first.provider} ${token ? "token" : "API key"}`}, kept on the board and never shown.`,
      button: `Connect ${names}`,
      service: services.length === 1 ? services[0] : null,
    };
  }
  if (w.type === "goal") return { title: "Track a goal", sub: "Counts a number toward its target: orders, revenue, visitors, or one you type.", button: "Connect", service: null };
  if (w.type === "metric" || w.type === "chart") return { title: w.type === "chart" ? "A number over time" : "A number to watch", sub: "Connect an app, any API or a feed, and it stays current while the board is open.", button: "Connect", service: null };
  return null;
}
